import {
    AddBankAccountRequest,
    BankAccountResponse,
    ConfigureDirectDepositRequest as ConfigureDirectDepositRequestBase
} from "@herbjoy/apex-api-models";
import { IHttpService, IPromise } from "angular";
import { DepositAmountType } from "../../../apex-main-app/roster/bank-accounts/deposit-amount-type";

/**
 * A service exposing data operations for interacting with an employee's bank accounts.
 */
export class BankAccountService {
    constructor(private $http: IHttpService, private apexApiUrl: string) {
        'ngInject';
    }

    /**
     * Get all bank accounts for the specific employee.
     * @param employeeID The API's identifier for the employee.
     */
    retrieveBankAccounts(employeeID: number): IPromise<BankAccountResponse[]> {
        return this.$http<BankAccountResponse[]>({
            method: "GET",
            url: `${this.apexApiUrl}/employees/${employeeID}/bank-accounts`
        }).then(p => p.data);
    }

    retrieveBankAccount(employeeID: number, accountID: number): IPromise<BankAccountResponse> {
        return this.$http<BankAccountResponse>({
            method: "GET",
            url: `${this.apexApiUrl}/employees/${employeeID}/bank-accounts/${accountID}`
        }).then(p => p.data);
    }

    /**
     * Create a new bank account and return the resulting bank account ID.
     */
    createBankAccount(employeeID: number, request: AddBankAccountRequest): IPromise<number> {
        return this.$http<number>({
            method: "POST",
            url: `${this.apexApiUrl}/employees/${employeeID}/bank-accounts`,
            data: request
        }).then(p => p.data);
    }

    /**
     * Enable and configure direct deposit for the given employee bank account.
     */
    configureDirectDeposit(employeeID: number, accountID: number, request: ConfigureDirectDepositRequest):
        IPromise<void> {
        const formData = createDirectDepositRequestFormData(request);
        return this.$http({
            method: "PUT",
            url: `${this.apexApiUrl}/employees/${employeeID}/bank-accounts/${accountID}/direct-deposit`,
            data: formData,
            // The following two lines are required to submit the request as multipart/form-data.
            transformRequest: id => id,
            headers: { "Content-Type": undefined }
        }).then(() => null);
    }

    /**
     * Cancel direct deposit for the given employee bank account.
     */
    cancelDirectDeposit(employeeID: number, accountID: number): IPromise<void> {
        return this.$http({
            method: "DELETE",
            url: `${this.apexApiUrl}/employees/${employeeID}/bank-accounts/${accountID}/direct-deposit`,
        }).then(() => null);
    }

    /**
     * Remove the bank account from the employee.
     */
    removeBankAccount(employeeID: number, accountID: number): IPromise<void> {
        return this.$http({
            method: "DELETE",
            url: `${this.apexApiUrl}/employees/${employeeID}/bank-accounts/${accountID}`,
        }).then(() => null);
    }

    /**
     * Return the list of deposit amount types that can be selected when configuring direct deposit.
     * @param employeeID The employee whose bank accounts can specify the deposit amount types
     */
    retrieveDepositAmountTypes(employeeID: number): IPromise<DepositAmountType[]> {
        return this.$http<DepositAmountType[]>({
            method: "GET",
            url: `${this.apexApiUrl}/employees/${employeeID}/deposit-amount-types`
        }).then(p => p.data);
    }
}

type Omit<T, K extends keyof T> = Pick<T, Exclude<keyof T, K>>
export type ConfigureDirectDepositRequest = Omit<ConfigureDirectDepositRequestBase, "VoidCheck" | "AdpDirectDeposit"> & {
    VoidCheck?: File;
    AdpDirectDeposit?: File;
};

function createDirectDepositRequestFormData(request: ConfigureDirectDepositRequest): FormData {
    const formData = new FormData();
    formData.append("DepositAmount", request.DepositAmount?.toString() ?? "")
    formData.append("DepositAmountTypeID", request.DepositAmountTypeID?.toString() ?? "")
    if (request.VoidCheck) {
        formData.append("VoidCheck", request.VoidCheck);
    }
    if (request.AdpDirectDeposit) {
        formData.append("AdpDirectDeposit", request.AdpDirectDeposit);
    }
    return formData;
}
